import axios from 'axios';

import config from '../../../config';
import { AskAnswer, ChatTurn, Computing, GargiFile, IntelligenceModule } from '../interfaces/models';
import { ChanModelCatalog } from '../../GargiJourneyAssistant/services';

const { apiServerUrl } = config;

// Tenant and business come from the signed-in user on the server; these requests only name what to compute.

export type ModuleResponse<T> = { ready: true; data: T } | { ready: false; computing: Computing };

export const getModule = <T>(module: IntelligenceModule, refresh = false): Promise<ModuleResponse<T>> =>
  axios
    .get(`${apiServerUrl}/users/intelligence/models/${module}`, { params: refresh ? { refresh: '1' } : {} })
    .then(({ status, data }) => (status === 202 ? { ready: false, computing: data as Computing } : { ready: true, data: data as T }));

export type DetailKind = 'store' | 'category';

// One store's or one category's drill-down from the day's computed module.
export const getDetail = <T>(kind: DetailKind, key: string): Promise<ModuleResponse<T>> =>
  axios
    .get(`${apiServerUrl}/users/intelligence/detail`, { params: { kind, key } })
    .then(({ status, data }) => (status === 202 ? { ready: false, computing: data as Computing } : { ready: true, data: data as T }));

// Progress while Gargi works: a new step starts ("turn"), answer text as it is written, a tool being used.
export interface AskProgress {
  type: 'turn' | 'text' | 'step' | 'heard';
  text?: string;
  tool?: string;
  input?: Record<string, unknown>;
}

type AskEvent = AskProgress | { type: 'done'; result: AskAnswer } | { type: 'error'; status: number; message: string };

// Splits newline-delimited JSON as it arrives; a partial last line waits for the next chunk.
export const ndjsonReader = (onEvent: (event: AskEvent) => void) => {
  let buffer = '';
  return (chunk: string) => {
    buffer += chunk;
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.filter((line) => line.trim()).forEach((line) => {
      try {
        onEvent(JSON.parse(line) as AskEvent);
      } catch {
        // A malformed line is skipped; the final result still arrives as its own line.
      }
    });
  };
};

// Ask Gargi with live progress. Errors carry the status codes of /ask (403, 503).
export const askGargiStream = async (
  question: string,
  history: ChatTurn[],
  context: Record<string, unknown> | undefined,
  onProgress: (event: AskProgress) => void,
  model?: string,
  files?: string[],
  conversation?: string,
): Promise<AskAnswer> => {
  let result: AskAnswer | null = null;
  let failure: { status: number; message: string } | null = null;
  const read = ndjsonReader((event) => {
    if (event.type === 'done') result = event.result;
    else if (event.type === 'error') failure = event;
    else onProgress(event);
  });
  let seen = 0;
  const response = await axios.post<string>(
    `${apiServerUrl}/users/intelligence/ask/stream`,
    { question, history: history.filter((t) => !t.error).slice(-10).map(({ role, content }) => ({ role, content })), context, ...(model ? { model } : {}), ...(files?.length ? { files } : {}), ...(conversation ? { conversation } : {}) },
    {
      timeout: 5 * 60_000,
      responseType: 'text',
      onDownloadProgress: (progress) => {
        const text = (progress.event?.target as XMLHttpRequest | undefined)?.responseText ?? '';
        read(text.slice(seen));
        seen = text.length;
      },
    },
  );
  read(`${String(response.data ?? '').slice(seen)}\n`);
  if (failure) {
    const { status, message } = failure;
    throw Object.assign(new Error(message), { response: { status, data: { message } } });
  }
  if (!result) throw new Error('Gargi did not finish the answer');
  return result;
};

// Attach a file to Ask Gargi (CSV, Excel, text, JSON, PDF or an image, up to 20 MB).
export const uploadGargiFile = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return axios.post<GargiFile>(`${apiServerUrl}/users/intelligence/files`, form, { timeout: 2 * 60_000 }).then(({ data }) => data);
};

// Save a file Gargi wrote (or one attached earlier) to the person's computer.
export const downloadGargiFile = async (file: Pick<GargiFile, 'id' | 'name'>) => {
  const { data } = await axios.get<Blob>(`${apiServerUrl}/users/intelligence/files/${file.id}`, { responseType: 'blob', timeout: 2 * 60_000 });
  saveBlob(data, file.name);
};

export const saveBlob = (data: Blob, name: string) => {
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
};

// Add to the question Gargi is still working on in this conversation; read at its next step.
// This person's Ask Gargi conversations for the business: saved after each answer, reopened to continue.
export interface SavedConversationSummary { id: string; title: string; updated: number; questions: number }
export const listConversations = (q = '') =>
  axios.get<{ conversations: SavedConversationSummary[] }>(`${apiServerUrl}/users/intelligence/conversations`, { params: q ? { q } : {} })
    .then(({ data }) => data.conversations);
export const getConversation = (id: string) =>
  axios.get<{ id: string; title: string; turns: ChatTurn[] }>(`${apiServerUrl}/users/intelligence/conversations/${id}`).then(({ data }) => data);
export const saveConversation = (id: string, turns: ChatTurn[]) =>
  axios.put(`${apiServerUrl}/users/intelligence/conversations/${id}`, { turns }).then(({ data }) => data);
export const deleteConversation = (id: string) =>
  axios.delete(`${apiServerUrl}/users/intelligence/conversations/${id}`).then(({ data }) => data);

export const interjectGargi = (conversation: string, text: string) =>
  axios.post(`${apiServerUrl}/users/intelligence/ask/interject`, { conversation, text }).then(({ data }) => data);

export interface AnalystStatus {
  ready: boolean;
  reason: string | null;
  model: string;
}

export const getSuggestions = () =>
  axios.get<{ questions: string[]; analyst?: AnalystStatus; models?: ChanModelCatalog }>(`${apiServerUrl}/users/intelligence/suggestions`).then(({ data }) => data);
